/**
 * list_flow_versions tool - List all versions of a flow
 */

import { z } from "zod";
import type { FlowManagementApi } from "../api/flow-management.js";
import { logger } from "../utils/logger.js";

export const listFlowVersionsInputSchema = z.object({
  flowId: z.string().describe("The flow ID (GUID)"),
  environment: z
    .string()
    .optional()
    .describe("Environment name or GUID. Uses default if omitted."),
});

export type ListFlowVersionsInput = z.infer<typeof listFlowVersionsInputSchema>;

export const listFlowVersionsTool = {
  name: "list_flow_versions",
  description:
    "List all versions of a flow. Each version represents a saved state of the flow definition. Use this to track changes or restore to a previous version.",
  inputSchema: {
    type: "object" as const,
    properties: {
      flowId: {
        type: "string",
        description: "The flow ID (GUID)",
      },
      environment: {
        type: "string",
        description: "Environment name or GUID. Uses default if omitted.",
      },
    },
    required: ["flowId"],
  },
};

export async function handleListFlowVersions(
  api: FlowManagementApi,
  input: unknown
): Promise<{ text: string; structured?: Record<string, unknown> }> {
  const parsed = listFlowVersionsInputSchema.parse(input);

  logger.info({ flowId: parsed.flowId }, "Executing list_flow_versions");

  try {
    const versions = await api.listFlowVersions(parsed.flowId, parsed.environment);

    if (versions.length === 0) {
      return { text: "No versions found for this flow.", structured: { count: 0, versions: [] } };
    }

    const lines = [`Flow Versions (${versions.length}):`];
    for (const version of versions) {
      lines.push(`  • Version: ${version.version}`);
      lines.push(`    Created: ${new Date(version.createdTime).toLocaleString()}`);
    }

    // Machine-parseable mirror of the prose, emitted as MCP structuredContent (#11).
    const structured: Record<string, unknown> = {
      count: versions.length,
      versions: versions.map((version) => ({
        id: version.id,
        version: version.version,
        createdTime: version.createdTime,
      })),
    };

    return { text: lines.join("\n"), structured };
  } catch (error) {
    const msg = error instanceof Error ? error.message : String(error);
    // The versions API endpoint may not be available for all flow types
    if (msg.includes("404") || msg.includes("not found")) {
      return {
        text: "Flow versions API not available. This endpoint may only work for solution-aware flows. For non-solution flows, version history is not accessible via the REST API. Consider using the Dataverse API for solution-aware flows.",
      };
    }
    throw error;
  }
}
